import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * URL-backed enum filter. Returns a value derived from `?key=...` validated
 * by `isValid`, plus a setter that uses `{ replace: true }` (filter changes
 * shouldn't pollute history). Passing the `defaultValue` deletes the param
 * instead of writing it, keeping default URLs clean.
 */
export const useEnumSearchParam = <T extends string>(
  key: string,
  isValid: (v: unknown) => v is T,
  defaultValue?: T
): [T | undefined, (next: T | undefined) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = useMemo<T | undefined>(() => {
    const v = searchParams.get(key);
    return isValid(v) ? v : defaultValue;
  }, [key, isValid, searchParams, defaultValue]);
  const setValue = useCallback(
    (next: T | undefined) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (!next || next === defaultValue) params.delete(key);
          else params.set(key, next);
          return params;
        },
        { replace: true }
      );
    },
    [key, setSearchParams, defaultValue]
  );
  return [value, setValue];
};
